/**
 * Componente riusabile: select del preventivo per i dialog "Aggiungi al preventivo".
 *
 * Logica UX (definita con Fabrizio):
 *  - **Default visibili**: solo preventivi `draft` (bozza) + `sent` (inviato)
 *  - **Nascosti**: `won` / `lost` / `contracted` (sono chiusi, modificarli è errato).
 *    Resta visibile un toggle in fondo per riaprirli (casi rari di copia da
 *    preventivo vinto, ecc.)
 *  - **Badge colorato per stato** (blu draft, giallo sent, verde won, rosso lost,
 *    grigio contracted + icona lucchetto)
 *  - **Tooltip warning su `sent`**: "Già inviato al cliente — modifica con cautela"
 *
 * Usato in: AddToEstimateDialogV2 (parete) e AddCeilingToEstimateDialog (controsoffitto).
 */
import React, { useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EstimateLike {
  id: string;
  name: string;
  status?: string;
  projectName?: string;
}

interface EstimateSelectFieldProps {
  estimates: EstimateLike[];
  value: string;
  onChange: (id: string) => void;
  label?: string;
  /** Placeholder quando nessun preventivo è selezionato. */
  placeholder?: string;
}

const STATUS_META: Record<string, { label: string; cls: string; isClosed: boolean }> = {
  draft:      { label: 'Bozza',    cls: 'bg-blue-50 text-blue-700 border-blue-200',         isClosed: false },
  sent:       { label: 'Inviato',  cls: 'bg-amber-50 text-amber-800 border-amber-200',       isClosed: false },
  won:        { label: 'Vinto',    cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', isClosed: true  },
  lost:       { label: 'Perso',    cls: 'bg-red-50 text-red-700 border-red-200',             isClosed: true  },
  contracted: { label: 'Firmato',  cls: 'bg-slate-100 text-slate-700 border-slate-300',      isClosed: true  },
};

const EstimateSelectField: React.FC<EstimateSelectFieldProps> = ({
  estimates,
  value,
  onChange,
  label = 'Preventivo',
  placeholder = 'Seleziona un preventivo…',
}) => {
  const [showClosed, setShowClosed] = useState(false);

  const { activeEstimates, closedCount } = useMemo(() => {
    const active: EstimateLike[] = [];
    let closed = 0;
    for (const e of estimates ?? []) {
      const meta = STATUS_META[e.status ?? ''];
      if (meta?.isClosed) {
        closed += 1;
        if (showClosed) active.push(e);
      } else {
        active.push(e);
      }
    }
    return { activeEstimates: active, closedCount: closed };
  }, [estimates, showClosed]);

  return (
    <div className="space-y-1.5">
      <Label htmlFor="est-select">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="est-select">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {activeEstimates.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              Nessun preventivo {showClosed ? '' : 'aperto'} disponibile.
              {!showClosed && closedCount > 0 && (
                <div className="mt-1">
                  ({closedCount} chiusi nascosti)
                </div>
              )}
            </div>
          ) : (
            activeEstimates.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                <EstimateRow estimate={e} />
              </SelectItem>
            ))
          )}

          {/* Footer toggle: mostra/nascondi chiusi (won/lost/contracted) */}
          {closedCount > 0 && (
            <div className="border-t mt-1 pt-1">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowClosed((v) => !v);
                }}
                className="w-full px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-zinc-100 rounded flex items-center gap-1.5 justify-center"
              >
                {showClosed ? (
                  <>
                    <EyeOff className="h-3 w-3" />
                    Nascondi chiusi
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" />
                    Mostra anche chiusi ({closedCount})
                  </>
                )}
              </button>
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

const EstimateRow: React.FC<{ estimate: EstimateLike }> = ({ estimate: e }) => {
  const meta = STATUS_META[e.status ?? ''] ?? STATUS_META.draft;
  const title = e.projectName ? `${e.projectName} — ${e.name}` : e.name;
  const isSent = e.status === 'sent';
  const isContracted = e.status === 'contracted';

  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <span className="truncate flex-1">{title}</span>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${meta.cls}`}>
        {meta.label}
      </Badge>
      {isSent && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[260px] text-xs">
              Preventivo già inviato al cliente — modificalo con cautela
              (le aggiunte non si riflettono in quello che il cliente ha già visto).
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isContracted && <Lock className="h-3 w-3 text-red-600 shrink-0" />}
    </div>
  );
};

export default EstimateSelectField;
